/**
 * «L'app è già installata?» — la domanda che nessun browser risponde davvero.
 *
 * Non esiste un segnale diretto. `isStandalone()` (in-app-browser.ts) dice solo
 * com'è aperta QUESTA finestra: da una scheda del browser è false anche quando
 * la PWA è installata da mesi. `getInstalledRelatedApps()` esiste su Chromium ma
 * vuole `related_applications` nel manifest ed è comunque cieco altrove.
 *
 * Qui si tiene la memoria che manca: la PRIMA volta che l'app gira in standalone
 * (o quando arriva `appinstalled`) lo si annota, e da quel momento anche la
 * scheda del browser sa che non c'è niente da proporre.
 *
 * Funziona su Android/Chrome e su desktop, dove la WebAPK condivide il
 * localStorage del profilo del browser.
 *
 * NON funziona su iOS: i web app della schermata Home hanno storage separato da
 * Safari, quindi questa memoria lì resta vuota per sempre. Il buco è coperto
 * altrove, e in due modi — perché nessuno dei due basta da solo:
 *   · sul PROFILO (migration 091 + POST /api/user/pwa-installed): l'app lo
 *     dice al server, e la scheda Safari dello stesso account lo rilegge dal
 *     sommario della dashboard. È l'unico canale che attraversa il confine
 *     degli storage, e copre chi dentro l'app ci entra davvero;
 *   · con la chiusura definitiva su iOS (MAI_PIU qui sotto), per chi l'app
 *     l'ha installata ma non ci ha mai fatto login.
 */
import { isStandalone } from './in-app-browser';

const INSTALLED_KEY = 'draughts-pwa-installed';
const SNOOZED_KEY = 'draughts-install-card-snoozed-until';
const REPORTED_KEY = 'draughts-pwa-reported';
const DISMISSALS_KEY = 'draughts-install-card-dismissals';

/** Chiusa la card, per un mese non se ne riparla. */
export const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Chiusure dopo le quali non si insiste più, ovunque.
 *
 * Rimandare di un mese ha senso finché è un promemoria; ripetuto all'infinito è
 * assillo. Chi ha chiuso la card tre volte ha già risposto. Il conto è per
 * browser, come lo snooze: è lì che la card si vede.
 */
export const MAX_DISMISSALS = 3;

/**
 * «Mai più», scritto al posto di una scadenza.
 *
 * Su Android e desktop rimandare di un mese è onesto, perché lì la card si
 * corregge da sola: se nel frattempo l'app viene installata lo sappiamo
 * (`appinstalled`, l'assenza di `beforeinstallprompt`, la memoria standalone) e
 * la card tace da sé. Su iOS no: lo storage del web app in schermata Home è
 * separato da Safari, quindi da una scheda non c'è NIENTE da cui accorgersi
 * dell'installazione. Riproporla ogni 30 giorni a chi l'ha già installata è
 * l'unico esito possibile, ed è il motivo per cui lì la chiusura è definitiva
 * subito. Altrove ci si arriva comunque, ma alla terza chiusura: vedi
 * MAX_DISMISSALS.
 */
const MAI_PIU = 'never';

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null; // Modalità privata / storage disabilitato.
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* Niente da fare: la card ricomparirà, che è il guasto meno grave. */
  }
}

/** Al boot: se stiamo girando dentro l'app installata, annotalo. */
export function markInstalledIfStandalone(): void {
  if (isStandalone()) markInstalled();
}

/** Dall'evento `appinstalled` (vedi install-prompt.ts). */
export function markInstalled(): void {
  write(INSTALLED_KEY, '1');
}

/**
 * Vero solo se l'installazione è stata osservata prima d'ora. Falso non vuol
 * dire «non installata»: vuol dire «non ne sappiamo niente».
 */
export function isKnownInstalled(): boolean {
  return read(INSTALLED_KEY) === '1';
}

export function isSnoozed(now: number): boolean {
  const salvato = read(SNOOZED_KEY);
  if (salvato === MAI_PIU) return true;
  const until = Number(salvato);
  // NaN (chiave assente o sporca) è false su entrambi i confronti: la card si vede.
  return Number.isFinite(until) && until > now;
}

/**
 * L'utente ha chiuso la card.
 *
 * Due strade verso lo stesso «mai più», per due ragioni diverse:
 *   · su iOS subito, perché lì la card non ha modo di accorgersi da sola di
 *     un'installazione e rimandare vorrebbe dire riproporla a chi l'app ce l'ha
 *     già (vedi MAI_PIU);
 *   · altrove alla terza chiusura, perché a quel punto non è più un promemoria.
 *
 * Il conto sale a ogni chiusura e non si azzera: è la chiusura stessa a
 * decidere, non il tempo passato.
 */
export function dismiss(now: number, platform: string): void {
  const chiusure = dismissalCount() + 1;
  write(DISMISSALS_KEY, String(chiusure));

  const perSempre = platform === 'ios' || chiusure >= MAX_DISMISSALS;
  write(SNOOZED_KEY, perSempre ? MAI_PIU : String(now + SNOOZE_MS));
}

/**
 * Quante volte la card è già stata chiusa in questo browser.
 *
 * Zero anche quando lo storage è vietato: il ripiego è lo snooze di 30 giorni,
 * cioè il comportamento di prima, non un silenzio definitivo preso per errore.
 */
export function dismissalCount(): number {
  const n = Number(read(DISMISSALS_KEY));
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/**
 * L'installazione è già stata riferita al server da QUESTA copia dell'app.
 *
 * Dentro l'app installata lo storage è stabile, quindi la POST parte una volta
 * sola nella vita di quell'installazione invece che a ogni avvio. Una
 * reinstallazione o uno storage ripulito la fanno rifare, ed è innocuo: la
 * rotta è idempotente.
 */
export function isInstallReported(): boolean {
  return read(REPORTED_KEY) === '1';
}

export function markInstallReported(): void {
  write(REPORTED_KEY, '1');
}
